import React, { useEffect, useState } from 'react';
import adminApi from '../../lib/adminApi';
import { Users, Eye, FileText, ShoppingBag } from 'lucide-react';

const Card = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid={testid}>
    <Icon className="w-5 h-5 text-purple-400 mb-3" />
    <p className="text-white text-2xl font-bold">{value}</p>
    <p className="text-gray-400 text-xs mt-1">{label}</p>
    {hint && <p className="text-gray-600 text-[11px] mt-2 leading-relaxed">{hint}</p>}
  </div>
);

const Bars = ({ title, rows, hint }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <h2 className="text-white text-base font-semibold mb-1">{title}</h2>
      {hint && <p className="text-gray-500 text-xs mb-4">{hint}</p>}
      {rows.length === 0 && <p className="text-gray-600 text-sm">Aucune donnée encore.</p>}
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-300 truncate max-w-[70%]">{r.label}</span>
              <span className="text-gray-500">{r.count}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-purple-500/70 rounded-full transition-all duration-700" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Analytics = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    adminApi.get(`/api/admin/analytics?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-white text-2xl font-bold">Audience du site</h1>
          <p className="text-gray-500 text-sm mt-1">Sans cookie ni traceur publicitaire : rien n'est transmis à un tiers.</p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-4 py-2 rounded-full text-xs transition-colors ${days === d ? 'bg-white text-black' : 'text-gray-400 border border-white/10 hover:text-white'}`}
                    data-testid={`analytics-range-${d}`}>
              {d} jours
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <p className="text-gray-500 text-sm">Chargement…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card icon={Users} label="Visiteurs" value={data.visitors} testid="analytics-visitors" />
            <Card icon={Eye} label="Pages vues" value={data.views} testid="analytics-views" />
            <Card icon={FileText} label="Demandes de devis" value={data.quotes}
                  hint={`${data.conversion} % des visiteurs demandent un devis`} testid="analytics-quotes" />
            <Card icon={ShoppingBag} label="Commandes" value={data.orders} testid="analytics-orders" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Bars title="D'où viennent vos visiteurs" rows={data.origins}
                  hint="Moteur de recherche = Google. Réseaux sociaux = Instagram, TikTok, YouTube…" />
            <Bars title="Pages les plus consultées" rows={data.pages} hint="Les pages qui intéressent vraiment vos visiteurs." />
          </div>

          <Bars title="Langues des visiteurs" rows={data.langs} hint="Utile pour savoir si le trafic étranger justifie plus de contenu traduit." />

          {data.perDay.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <h2 className="text-white text-base font-semibold mb-4">Visiteurs par jour</h2>
              <div className="flex items-end gap-1 h-32">
                {data.perDay.map((d) => {
                  const max = Math.max(1, ...data.perDay.map((x) => x.visitors));
                  return (
                    <div key={d.day} className="flex-1 bg-purple-500/60 rounded-t hover:bg-purple-400 transition-colors"
                         style={{ height: `${(d.visitors / max) * 100}%` }} title={`${d.day} — ${d.visitors} visiteurs`} />
                  );
                })}
              </div>
              <p className="text-gray-600 text-[11px] mt-3">Survolez une barre pour voir la date et le nombre de visiteurs.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Analytics;
